<template>
	
	<!-- Main Sidebar -->
	<a-layout-sider
		collapsible
		class="sider-primary"
		breakpoint="lg"
		collapsed-width="0"
		width="256px"
		:collapsed="sidebarCollapsed"
		@collapse="$emit('toggleSidebar', ! sidebarCollapsed)"
		:trigger="null"
		:class="['ant-layout-sider-' + sidebarColor, 'ant-layout-sider-' + sidebarTheme]"
		theme="light"
		:style="{ backgroundColor: 'transparent',}">
			<!-- Brand Area with Logo SVG -->
			<div class="brand">
				<div class="brand-logo">
					<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
						<path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>
					</svg>
				</div>
				<span class="brand-text">艾捷思育种</span>
			</div>

			<a-menu mode="inline" width="100%">
				<template v-for="item in filteredMenuItems">
					<!-- 无子菜单项 -->
					<a-menu-item 
						v-if="!item.children"
						:key="item.path"
						v-permission="item.permission">
						<router-link :to="item.path" class="menu-title-link">
							<span class="icon">
								<svg-icon :name="item.icon" :paths="icons[item.icon]"/>
							</span>
							<span class="label">{{ item.title }}</span>
						</router-link>
					</a-menu-item>
					
					<!-- 有子菜单项 -->
					<a-sub-menu 
						v-if="item.children"
						:key="item.title">
						<template slot="title">
							<span>
								<span class='icon'>
									<svg-icon :name='item.icon' :paths='icons[item.icon]' />
								</span>
								<span>{{ item.title }}</span>
							</span>
						</template>
						<a-menu-item v-for="child in item.children" :key="child.path" v-permission="child.permission">
							<router-link :to="child.path" class="menu-title-link" v-if="!child.role_code || (child.role_code && child.role_code.includes(info.role.code))">
								<span class="icon">
									<svg-icon :name="child.icon || item.icon" :paths="icons[child.icon] || icons[item.icon]" />
								</span>
								<span class="label menu-item-title">{{ child.title }}</span>
							</router-link>
						</a-menu-item>
						<a-menu-item style="text-align: center;" v-if="item.title=='牧场管理'">
							<a-button type="primary" shape="round" size="small" @click="toFarm" class="margin-top-10">管理</a-button>
						</a-menu-item>
					</a-sub-menu>
				</template>
			</a-menu>
	</a-layout-sider>
</template>

<script>
import SvgIcon from '../../views/components/SvgIcon.vue';
import { mapGetters } from 'vuex';
import util from '@/libs/util.js';

// 检查是否有权限（支持检查子权限）
const checkPermission = (permissionList) => {
  if (!permissionList || permissionList.length === 0) {
    return true;
  }
  
  const permissions = util.cookies.get('permissions');
  if (!permissions) {
    return false;
  }
  
  const permissionsArr = permissions.split(',');
  const roleList = Array.isArray(permissionList) ? permissionList : [String(permissionList)];
  
  return roleList.some(e => permissionsArr.includes(String(e)));
};

// 检查菜单项是否有权限（包括子菜单的权限检查）
// parentPermission: 父菜单的权限要求，用于子菜单继承
const hasMenuPermission = (item, parentPermission = null) => {
  // 确定当前菜单项的有效权限要求：优先使用自己的权限，否则继承父权限
  const effectivePermission = item.permission && item.permission.length > 0 
    ? item.permission 
    : parentPermission;
  
  // 如果有子菜单，先检查是否有任意子菜单有权限
  // 这样即使父菜单没有权限，只要有子菜单有权限，父菜单也会显示
  if (item.children && item.children.length > 0) {
    return item.children.some(child => hasMenuPermission(child, effectivePermission));
  }
  
  // 如果没有有效权限要求，默认有权限（顶级菜单项没有设置权限时）
  if (!effectivePermission || effectivePermission.length === 0) {
    return true;
  }
  
  // 检查当前菜单项的有效权限（叶子节点）
  return checkPermission(effectivePermission);
};

	export default ({
		components: {
			SvgIcon
		},
		props: {
			// Sidebar collapsed status.
			sidebarCollapsed: {
				type: Boolean,
				default: false,
			},
			
			// Main sidebar color.
			sidebarColor: {
				type: String,
				default: "primary",
			},
			
			// Main sidebar theme : light, white, dark.
			sidebarTheme: {
				type: String,
				default: "light",
			},
		},
		data() {
			return {
				menuItems: [
					{
						title: '首页',
						icon: 'dashboard',
						path: '/dashboard',
					},
					{
						title: '个体信息',
						icon: 'breed_items',
						path: '/breed_items',
						permission: ['13001']
					},
					{
						title: '育种计划',
						icon: 'yu_zhong',
						permission: ['18000'],
						children: [
							{ title: '计划列表', path: '/yu_zhong/yu_zhong_list', icon: 'yu_zhong'},
							{ title: '批量匹配', path: '/yu_zhong/batch_yu_zhong', icon: 'batch_match'},
							{ title: '单次匹配', path: '/yu_zhong/single_yu_zhong', icon: 'single_match' },
							{ title: '家系图谱', path: '/yu_zhong/family_graph', icon: 'family_graph', permission: ['14001'] },
						],
					},
					{
						title: '牧场管理',
						icon: 'farms',
						path: '/farms',
						permission: ['15000'],
						children: []
					},
					{
						title: '事件管理',
						icon: 'events',
						children: [
							{ title: '事件类型', path: '/events/types', icon: 'event_type'},
							{ title: '事件列表', path: '/events/records', icon: 'event_list'},
						],
						permission: ['19000']
					},
					{
						title: '控制台',
						icon: 'dashboard',
						path: '/console',
						permission: ['12004'],
						children: [
							{ title: '权限管理', path: '/console/permissions', icon: 'permissions', permission: ['12004'] },
							{ title: '角色管理', path: '/console/roles', icon: 'roles', permission: ['12004'] },
						]	
					},
					{
						title: '监控大屏',
						icon: 'dashboard',
						path: '/monitoring-dashboard',
						permission: ['16000']
					},
					{
						title: '组织管理',
						icon: 'farms',
						permission: ['17000'],
						children: [
							{ title: '组织列表', path: '/tenant/organizations', icon: 'farms', permission: ['17001'] },
							{ title: '用户管理', path: '/tenant/users', icon: 'breed_items', permission: ['17002'] }
						]
					}
				],
				icons: {
					// 首页 - house icon (stroke style)
					dashboard: [
						"M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8",
						"M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
					],
					// 个体信息 - database icon (stroke style)
					breed_items: [
						"M12 2C6.5 2 2 4 2 6.5C2 9 6.5 11 12 11C17.5 11 22 9 22 6.5C22 4 17.5 2 12 2Z",
						"M2 6.5V12C2 14.5 6.5 16.5 12 16.5C17.5 16.5 22 14.5 22 12V6.5",
						"M2 12V17.5C2 20 6.5 22 12 22C17.5 22 22 20 22 17.5V12"
					],
					// 育种计划 - calendar icon (stroke style)
					yu_zhong: [
						"M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z",
						"M16 2V6",
						"M8 2V6",
						"M3 10H21"
					],
					// 批量匹配 - users icon (stroke style)
					batch_match: [
						"M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2",
						"M16 3.128a4 4 0 0 1 0 7.744",
						"M22 21v-2a4 4 0 0 0-3-3.87",
						"M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
					],
					// 单次匹配 - link icon (stroke style)
					single_match: [
						"M9 17H7A5 5 0 0 1 7 7h2",
						"M15 7h2a5 5 0 1 1 0 10h-2",
						"M8 12h8"
					],
					// 家系图谱 - git-branch icon (stroke style)
					family_graph: [
						"M6 3v12",
						"M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
						"M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
						"M18 9a9 9 0 0 1-9 9"
					],
					// 牧场管理 - map icon (stroke style)
					farms: [
						"M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z",
						"M15 5.764v15",
						"M9 3.236v15"
					],
					// 事件管理 - bell icon (stroke style)
					events: [
						"M10.268 21a2 2 0 0 0 3.464 0",
						"M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"
					],
					// 事件类型 - tag icon (stroke style)
					event_type: [
						"M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z",
						"M7 7h.01"
					],
					// 事件列表 - list icon (stroke style)
					event_list: [
						"M8 6h13",
						"M8 12h13",
						"M8 18h13",
						"M3 6h.01",
						"M3 12h.01",
						"M3 18h.01"
					],
					// 权限管理 - shield icon (stroke style)
					permissions: [
						"M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
					],
					// 角色管理 - user icon (stroke style)
					roles: [
						"M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2",
						"M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
					]
				}
			}
		},
		created() {
			this.getFarms()
		},
		computed: {
			// 	return this.$store.getters['farm/farms'] || []
			// }
			...mapGetters('main/farm', [ 'farms' ]),
			...mapGetters('main/user', [ 'info' ]),
			// 计算过滤后的菜单项
			filteredMenuItems() {
				// 安全检查：如果用户信息还没准备好，返回所有菜单项
				const roleCode = this.info?.role?.code;
				return this.menuItems.filter(item => {
					// 检查 role_code 过滤（如果有 role_code 要求但用户角色不匹配）
					if (item.role_code && roleCode !== undefined && !item.role_code.includes(roleCode)) {
						return false;
					}
					// 检查权限（包括子权限）
					return hasMenuPermission(item);
				});
			}
		},
		methods: {
			renderSubMenuTitle(item) {
				return (
					<span>
						<span class="icon">
							<svg-icon name={item.icon} paths={this.icons[item.icon]} />
						</span>
						<span>{item.title}</span>
					</span>
				)
			},
			async getFarms() {
				await this.$store.dispatch('main/farm/getFarms')
				
				this.menuItems.find(item => item.title === '牧场管理').children = this.farms.map(item => {
					return {
						path: `/farms/${item.id}`,
						icon: 'farms',
						title: item.name
					}
				})
				console.log(this.menuItems);
			},
			toFarm() {	
				this.$router.push('/farms/list')
			}
		},
		watch: {
			farms: {
				handler(newFarms) {
					this.menuItems[3].children = newFarms.map(item => {
						return {
							path: `/farms/${item.id}`,
							icon: 'farms',
							title: item.name
						}
					})
				},
				immediate: true
			}
		}
	})

</script>

<style lang="scss" scoped>
// ============================================
// Sidebar Styles - Facebook Engineering Standard
// Using BEM-like naming and proper specificity
// ============================================

// Design Tokens
$sidebar-width: 256px;
$header-height: 64px;
$primary: #2563EB;
$text-primary: #111827;
$text-secondary: #6B7280;
$border-color: #E5E7EB;
$bg-hover: #F9FAFB;
$menu-item-height: 48px;
$menu-padding-x: 16px;
$menu-padding-y: 12px;
$menu-gap: 4px;
$sidebar-padding: 12px;

// ============================================
// SIDEBAR CONTAINER
// ============================================
::v-deep .ant-layout-sider {
	background: #FFFFFF !important;
	border-right: 1px solid $border-color;
	overflow: hidden !important;
	// 禁用默认过渡动画，防止首次加载时布局闪烁
	transition: none !important;
}

::v-deep .ant-layout-sider-children {
	display: flex;
	flex-direction: column;
	height: 100%;
	overflow-x: hidden !important;
	// 禁用过渡动画
	transition: none !important;
}

// ============================================
// BRAND AREA - With gradient background
// ============================================
.brand {
	display: flex;
	align-items: center; // 确保图标和文字垂直居中
	gap: 12px;
	height: $header-height;
	padding: 0 24px;
	flex-shrink: 0;
	background: linear-gradient(to right, rgba(37, 99, 235, 0.05), transparent);
	border-bottom: 1px solid $border-color;
	position: relative;
	left: 0;
	top: 0;
	transform: none;
	box-sizing: border-box;
	width: 100%;
	margin-top: 0; // 确保没有上边距
	
	.brand-logo {
		width: 32px;
		height: 32px;
		min-width: 32px;
		min-height: 32px;
		border-radius: 8px;
		background: $primary;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #FFFFFF;
		flex-shrink: 0;
		box-sizing: border-box;
		margin: 0; // 确保没有边距
		
		// 确保 SVG 图标有正确尺寸
		svg {
			width: 20px;
			height: 20px;
			min-width: 20px;
			min-height: 20px;
			display: block;
			flex-shrink: 0;
		}
	}
	
	.brand-text {
		font-size: 18px;
		font-weight: 700;
		color: $primary;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
		letter-spacing: -0.01em;
		line-height: 32px; // 与图标高度一致，确保垂直对齐
		white-space: nowrap;
		overflow: hidden;
		margin: 0; // 确保没有边距
	}
}

// ============================================
// MENU CONTAINER
// ============================================
::v-deep .ant-menu {
	background: transparent !important;
	border: none !important;
	box-shadow: none !important;
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden !important;
	padding: $sidebar-padding;
	
	&::-webkit-scrollbar {
		width: 4px;
	}
	
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	
	&::-webkit-scrollbar-thumb {
		background: $border-color;
		border-radius: 2px;
	}
}

// ============================================
// MENU ITEMS - Base styles
// ============================================
::v-deep .ant-menu-item,
::v-deep .ant-menu-submenu > .ant-menu-submenu-title {
	padding: 0 !important;
	margin: 0 0 $menu-gap 0 !important;
	height: auto !important;
	line-height: 1 !important;
	border-radius: 16px !important;
	width: 100% !important;
	max-width: calc(#{$sidebar-width} - #{$sidebar-padding * 2}) !important;
	box-sizing: border-box !important;
	
	&::after {
		display: none !important;
	}
}

// ============================================
// MENU LINK WRAPPER
// ============================================
.menu-title-link {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: $menu-padding-y $menu-padding-x;
	border-radius: 16px;
	transition: all 0.15s ease;
	cursor: pointer;
	text-decoration: none;
	width: 100%;
	box-sizing: border-box;
	
	.icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		color: $text-secondary;
		transition: color 0.15s ease;
	}
	
	.label {
		font-size: 14px;
		color: $text-secondary;
		font-weight: 400;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		transition: color 0.15s ease;
	}
	
	&:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
}

// ============================================
// SELECTED STATE - Fixed overflow issue
// ============================================
::v-deep .ant-menu-item {
	&.ant-menu-item-selected {
		background-color: transparent !important;
		
		.menu-title-link {
			background-color: $primary;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
			
			.icon {
				color: #FFFFFF !important;
			}
			
			.label {
				color: #FFFFFF !important;
				font-weight: 500;
			}
			
			// Force SVG stroke color
			.icon svg {
				stroke: #FFFFFF !important;
			}
			
			&:hover {
				background-color: $primary;
			}
		}
	}
}

// ============================================
// SUBMENU STYLES
// ============================================
::v-deep .ant-menu-sub {
	background: transparent !important;
	padding: 0 0 0 16px !important;
	margin: 0 0 $menu-gap 0 !important;
	
	.ant-menu-item {
		margin: 0 0 $menu-gap 0 !important;
		width: 100% !important;
		max-width: 100% !important;
		
		.menu-title-link {
			padding: 10px $menu-padding-x;
			
			.icon {
				width: 18px;
				height: 18px;
			}
			
			.label {
				font-size: 14px;
			}
		}
		
		&.ant-menu-item-selected {
			background-color: transparent !important;
			
			.menu-title-link {
				background-color: $primary;
				box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
				
				.icon {
					color: #FFFFFF !important;
				}
				
				.label {
					color: #FFFFFF !important;
					font-weight: 500;
				}
				
				.icon svg {
					stroke: #FFFFFF !important;
				}
			}
		}
	}
}

// ============================================
// SUBMENU TITLE
// ============================================
::v-deep .ant-menu-submenu-title {
	padding: 0 !important;
	margin: 0 0 $menu-gap 0 !important;
	border-radius: 16px !important;
	height: auto !important;
	line-height: 1 !important;
	width: 100% !important;
	max-width: 100% !important;
	
	> span {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: $menu-padding-y $menu-padding-x;
		width: 100%;
		box-sizing: border-box;
	}
	
	.icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		color: $text-secondary;
		transition: color 0.15s ease;
	}
	
	// Text label in submenu title
	> span > span:last-child {
		font-size: 14px;
		font-weight: 400;
		color: $text-secondary;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
	}
	
	&:hover {
		background-color: rgba(0, 0, 0, 0.05) !important;
	}
}

// Expanded submenu title (active parent)
::v-deep .ant-menu-submenu-open > .ant-menu-submenu-title {
	.icon {
		color: $primary !important;
	}
	
	> span > span:last-child {
		color: $primary !important;
		font-weight: 500;
	}
	
	.icon svg {
		stroke: $primary !important;
	}
}

// Submenu arrow
::v-deep .ant-menu-submenu-arrow {
	color: $text-secondary !important;
	right: 16px !important;
}

::v-deep .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-submenu-arrow {
	color: $primary !important;
}

// Menu item hover - reset Ant Design default
::v-deep .ant-menu-item {
	&:hover {
		background-color: transparent !important;
	}
}

::v-deep .ant-menu-submenu {
	&:hover {
		background-color: transparent !important;
	}
}

// ============================================
// FARM MANAGEMENT BUTTON
// ============================================
::v-deep .ant-btn-primary {
	background-color: $primary !important;
	border-color: $primary !important;
	
	&:hover {
		background-color: darken($primary, 8%) !important;
		border-color: darken($primary, 8%) !important;
	}
}

// Utility classes
.menu-item-title {
	display: inline-block;
	max-width: 140px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.margin-top-10 {
	margin-top: 10px !important;
}
</style>
